import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, ApiError, setCsrfToken } from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({ isSuperAdmin: false, can: {} });
  const [loading, setLoading] = useState(true);

  const applyAuth = (data) => {
    setCsrfToken(data?.csrfToken);
    setUser(data?.user || null);
    setPermissions(data?.permissions || { isSuperAdmin: false, can: {} });
  };

  const refresh = useCallback(async () => {
    try {
      applyAuth(await api.get('/api/auth/me'));
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        applyAuth(null);
      } else {
        console.error('auth/me failed', e);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (identifier, password, remember = false) => {
    const data = await api.post('/api/auth/login', { identifier, password, remember });
    applyAuth(data);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post('/api/auth/logout'); }
    finally { applyAuth(null); }
  }, []);

  /**
   * useCan(key, action) -> bool. Супер-админ всегда может.
   */
  const can = useCallback(
    (key, action) => {
      if (!user) return false;
      if (permissions.isSuperAdmin) return true;
      return Boolean(permissions.can?.[key]?.[action]);
    },
    [user, permissions]
  );

  const value = useMemo(
    () => ({ user, permissions, loading, login, logout, refresh, can }),
    [user, permissions, loading, login, logout, refresh, can]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** Удобный шорткат, чтобы не таскать целиком useAuth там, где нужны только проверки прав. */
export function useCan() {
  return useAuth().can;
}
